import {
  type UseVirtualFloatingOptions,
  flip,
  offset,
} from "@platejs/floating";
import { getLinkAttributes } from "@platejs/link";
import {
  type LinkFloatingToolbarState,
  FloatingLinkUrlInput,
  LinkPlugin,
  submitFloatingLink,
  useFloatingLinkEdit,
  useFloatingLinkEditState,
  useFloatingLinkInsert,
  useFloatingLinkInsertState,
} from "@platejs/link/react";
import CheckIcon from "@redotech/redo-icons/check.svg";
import LinkIcon from "@redotech/redo-icons/link-01_filled.svg";
import LinkBrokenIcon from "@redotech/redo-icons/link-broken-01_filled.svg";
import LinkExternalIcon from "@redotech/redo-icons/link-external-02.svg";
import TextInputIcon from "@redotech/redo-icons/text-input.svg";
import type { TLinkElement } from "platejs";
import { KEYS } from "platejs";
import {
  useEditorPlugin,
  useEditorRef,
  useEditorSelection,
  useFormInputProps,
} from "platejs/react";
import {
  type ComponentType,
  type ReactNode,
  type SVGProps,
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { RedoIcon } from "../../arbiter-components/icon/redo-icon";
import { Flex } from "../../flex";
import { cn } from "../../tailwind-utils";
import { buttonVariants } from "../ui/button";
import { Separator } from "../ui/separator";
import { EDITOR_OVERLAY_Z_CLASS } from "./editor-overlay";

const popoverClassName = cn(
  EDITOR_OVERLAY_Z_CLASS,
  "w-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md outline-hidden",
);

export const linkFloatingToolbarFieldClassName =
  "flex h-[28px] w-full rounded-md border-none bg-transparent px-1.5 py-1 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-transparent";

export const LinkFloatingToolbarRow = memo(function LinkFloatingToolbarRow({
  Icon,
  children,
}: {
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  children: ReactNode;
}) {
  return (
    <Flex align="center">
      <Flex align="center" className="pr-1 pl-2 text-muted-foreground">
        <RedoIcon Icon={Icon} size={16} />
      </Flex>
      {children}
    </Flex>
  );
});

export interface LinkFloatingToolbarProps {
  state?: LinkFloatingToolbarState;
  insertExtension?: (
    props: LinkFloatingToolbarInsertExtensionProps,
  ) => ReactNode;
}

/** A generic hook point for editor-specific insert-link choices. */
export interface LinkFloatingToolbarInsertExtensionProps {
  setLink: (link: { url: string; text: string }) => void;
  /** While expanded the extension replaces the whole popover, not just its row. */
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
}

export const LinkFloatingToolbar = memo(function LinkFloatingToolbar({
  state,
  insertExtension,
}: LinkFloatingToolbarProps) {
  const floatingOptions: UseVirtualFloatingOptions = useMemo(
    () => ({
      middleware: [
        offset(8),
        flip({
          fallbackPlacements: ["bottom-end", "top-start", "top-end"],
          padding: 12,
        }),
      ],
      placement: "bottom-start" as const,
    }),
    [],
  );

  const insertState = useFloatingLinkInsertState({
    ...state,
    floatingOptions: { ...floatingOptions, ...state?.floatingOptions },
  });
  const {
    hidden,
    props: insertProps,
    ref: insertRef,
    textInputProps,
  } = useFloatingLinkInsert(insertState);

  const editState = useFloatingLinkEditState({
    ...state,
    floatingOptions: { ...floatingOptions, ...state?.floatingOptions },
  });
  const {
    editButtonProps,
    props: editProps,
    ref: editRef,
    unlinkButtonProps,
  } = useFloatingLinkEdit(editState);

  const editor = useEditorRef();
  const { setOption } = useEditorPlugin(LinkPlugin);
  const [extensionExpanded, setExtensionExpanded] = useState(false);
  const inputProps = useFormInputProps({ preventDefaultOnEnterKeydown: true });
  const urlInputRef = useRef<HTMLInputElement | null>(null);
  const textInputRef = useRef<HTMLInputElement | null>(null);
  const setLink = useCallback(
    ({ url, text }: { url: string; text: string }) => {
      setOption("url", url);
      setOption("text", text);
      if (urlInputRef.current) {
        urlInputRef.current.value = url;
      }
      if (textInputRef.current) {
        textInputRef.current.value = text;
      }
    },
    [setOption],
  );

  if (hidden) {
    // Reopening the popover should always start on the link fields, never on
    // whatever view the extension was left showing.
    if (extensionExpanded) {
      setExtensionExpanded(false);
    }
    return null;
  }

  const input = (
    <Flex
      className={cn("w-80", extensionExpanded && "h-36")}
      dir="column"
      {...inputProps}
    >
      {extensionExpanded && insertExtension ? (
        insertExtension({
          setLink,
          expanded: true,
          setExpanded: setExtensionExpanded,
        })
      ) : (
        <>
          <LinkFloatingToolbarRow Icon={LinkIcon}>
            <FloatingLinkUrlInput
              className={linkFloatingToolbarFieldClassName}
              data-plate-focus
              placeholder="Paste link"
              ref={urlInputRef}
            />
          </LinkFloatingToolbarRow>
          <Separator className="my-1" />
          <LinkFloatingToolbarRow Icon={TextInputIcon}>
            <input
              className={linkFloatingToolbarFieldClassName}
              data-plate-focus
              placeholder="Text to display"
              {...textInputProps}
              ref={(element) => {
                textInputRef.current = element;
                if (element) {
                  textInputProps.ref(element);
                }
              }}
            />
          </LinkFloatingToolbarRow>
          {insertExtension && (
            <>
              <Separator className="my-1" />
              {insertExtension({
                setLink,
                expanded: false,
                setExpanded: setExtensionExpanded,
              })}
            </>
          )}
          <Separator className="my-1" />
          <Flex className="p-1" justify="flex-end">
            <button
              className={cn(buttonVariants({ size: "sm", variant: "default" }))}
              onMouseDown={(e) => {
                e.preventDefault();
                submitFloatingLink(editor);
              }}
              type="button"
            >
              <div className="mr-1">
                <RedoIcon Icon={CheckIcon} size={14} />
              </div>
              Apply
            </button>
          </Flex>
        </>
      )}
    </Flex>
  );

  const editContent = editState.isEditing ? (
    input
  ) : (
    <div className="box-content flex items-center">
      <button
        className={cn(buttonVariants({ size: "sm", variant: "ghost" }))}
        type="button"
        {...editButtonProps}
      >
        Edit link
      </button>
      <Separator orientation="vertical" />
      <LinkOpenButton />
      <Separator orientation="vertical" />
      <button
        className={cn(buttonVariants({ size: "sm", variant: "ghost" }))}
        type="button"
        {...unlinkButtonProps}
      >
        <RedoIcon Icon={LinkBrokenIcon} size={16} />
      </button>
    </div>
  );

  return (
    <>
      <div className={popoverClassName} ref={insertRef} {...insertProps}>
        {input}
      </div>
      <div className={popoverClassName} ref={editRef} {...editProps}>
        {editContent}
      </div>
    </>
  );
});

const LinkOpenButton = memo(function LinkOpenButton() {
  const editor = useEditorRef();
  const selection = useEditorSelection();

  const attributes = useMemo(() => {
    const entry = editor.api.node<TLinkElement>({
      match: { type: editor.getType(KEYS.link) },
    });
    if (!entry) {
      return {};
    }
    const [element] = entry;
    return getLinkAttributes(editor, element);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, selection]);

  return (
    <a
      {...attributes}
      aria-label="Open link in a new tab"
      className={cn(buttonVariants({ size: "sm", variant: "ghost" }))}
      onMouseOver={(e) => {
        e.stopPropagation();
      }}
      target="_blank"
    >
      <RedoIcon Icon={LinkExternalIcon} size={16} />
    </a>
  );
});
